import {
  CAPTURE_METHOD_LABELS,
  CaptureMethod,
  EXHIBIT_SET_SCHEMA_VERSION,
  ExhibitPhotoManifestEntry,
  ExhibitPhoto,
  ExhibitSet,
  ExhibitSetManifest,
} from './exhibit-set.model';
import { isResolvableTimeZone } from '../../shared/Utility/timeZoneStatus';
import { AppliedOrientation, isQuarterTurn, NO_DEVICE_ORIENTATION, RotationMethod, RotationSource } from './capture-orientation';

export const FALLBACK_TIME_ZONE = 'UTC';

export const EXHIBIT_SET_ROOT_CLASS = 'fn-exhibit-set';
export const EXHIBIT_SET_MARKER_ATTR = 'data-fn-exhibit-set';
export const EXHIBIT_REFERENCE_ATTR = 'data-fn-exhibit-ref';
export const EXHIBIT_SUBJECT_ATTR = 'data-fn-exhibit-subject';
export const EXHIBIT_LOCATION_ATTR = 'data-fn-exhibit-location';
export const EXHIBIT_PHOTO_ROW_ATTR = 'data-fn-photo-seq';
export const EXHIBIT_PHOTO_FILE_ATTR = 'data-fn-photo-file';
export const EXHIBIT_PHOTO_SHA_ATTR = 'data-fn-photo-sha256';
export const EXHIBIT_PHOTO_SHA512_ATTR = 'data-fn-photo-sha512';
export const EXHIBIT_PHOTO_TIME_ATTR = 'data-fn-photo-captured';
export const EXHIBIT_PHOTO_BYTES_ATTR = 'data-fn-photo-bytes';
export const EXHIBIT_PHOTO_FILE_TIME_ATTR = 'data-fn-photo-file-time';
export const EXHIBIT_PHOTO_METHOD_ATTR = 'data-fn-photo-method';
export const EXHIBIT_PHOTO_ROTATION_ATTR = 'data-fn-photo-rotation';
export const EXHIBIT_PHOTO_ROTATION_SOURCE_ATTR = 'data-fn-photo-rotation-source';
export const EXHIBIT_PHOTO_ROTATION_METHOD_ATTR = 'data-fn-photo-rotation-method';
export const EXHIBIT_PHOTO_DEVICE_ORIENTATION_ATTR = 'data-fn-photo-device-orientation';
export const EXHIBIT_PHOTO_EXIF_EDIT_ATTR = 'data-fn-photo-exif-edit';
export const EXHIBIT_PHOTO_CAMERA_SHA_ATTR = 'data-fn-photo-camera-sha256';

const HTML_ESCAPES: Readonly<Record<string, string>> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

export function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (ch) => HTML_ESCAPES[ch]);
}

export function safeTimeZone(timeZone: string | null | undefined): string {
  return typeof timeZone === 'string' && timeZone.trim() !== '' && isResolvableTimeZone(timeZone) ? timeZone : FALLBACK_TIME_ZONE;
}

export function formatCaptureTime(iso: string, timeZone: string): string {
  const date = new Date(iso);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: safeTimeZone(timeZone),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
    timeZoneName: 'short',
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes): string => parts.find((p) => p.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')} ${part('hour')}:${part('minute')}:${part('second')} ${part('timeZoneName')}`;
}

export const CAMERA_APP_CAVEAT =
  'Photos from the device camera app show when the app received them and the file time the device reported; ' +
  'the device may let a user choose an existing photo instead of taking a new one.';

function describeCaptureMethods(set: ExhibitSet): string {
  const methods = Array.from(new Set(set.photos.map((p) => p.captureMethod)));
  const labels = methods.map((m: CaptureMethod) => CAPTURE_METHOD_LABELS[m]).join('; ');
  return methods.includes(CaptureMethod.CameraApp) ? `${labels}. ${CAMERA_APP_CAVEAT}` : labels;
}

function describeHistory(set: ExhibitSet): string {
  const notes: string[] = [];
  if (set.retakeCount > 0) {
    notes.push(`Photo 1 retaken ${set.retakeCount} time${set.retakeCount === 1 ? '' : 's'} before photo 2 was taken.`);
  }
  if (set.restartCount > 0) {
    notes.push(
      `Set restarted ${set.restartCount} time${set.restartCount === 1 ? '' : 's'} before saving; ` +
        `${set.discardedPhotoCount} photo${set.discardedPhotoCount === 1 ? '' : 's'} discarded.`
    );
  }
  if (notes.length === 0) {
    notes.push('No retakes or restarts.');
  }
  return notes.join(' ');
}

function metaRow(label: string, value: string): string {
  return `<tr><th>${escapeHtml(label)}</th><td>${escapeHtml(value)}</td></tr>`;
}

export function describePhotoTime(photo: ExhibitPhoto, timeZone: string): string {
  const captured = formatCaptureTime(photo.capturedAtIso, timeZone);
  if (photo.captureMethod !== CaptureMethod.CameraApp) {
    return captured;
  }
  const fileTime = photo.deviceFileTimeIso ? formatCaptureTime(photo.deviceFileTimeIso, timeZone) : 'not reported';
  return `Received ${captured}; device file time ${fileTime}`;
}

function optionalAttr(name: string, value: string | number | undefined): string {
  return value === undefined ? '' : ` ${name}="${escapeHtml(String(value))}"`;
}

function orientationAttrs(orientation: AppliedOrientation | undefined): string {
  return orientation
    ? ` ${EXHIBIT_PHOTO_ROTATION_ATTR}="${orientation.captureRotation}" ${EXHIBIT_PHOTO_ROTATION_SOURCE_ATTR}="${orientation.rotationSource}" ${EXHIBIT_PHOTO_ROTATION_METHOD_ATTR}="${orientation.method}"` +
        optionalAttr(EXHIBIT_PHOTO_DEVICE_ORIENTATION_ATTR, orientation.deviceOrientation) +
        optionalAttr(EXHIBIT_PHOTO_EXIF_EDIT_ATTR, orientation.exifEdit) +
        optionalAttr(EXHIBIT_PHOTO_CAMERA_SHA_ATTR, orientation.cameraSha256)
    : '';
}

function photoRow(set: ExhibitSet, index: number, timeZone: string): string {
  const photo = set.photos[index];
  return (
    `<tr ${EXHIBIT_PHOTO_ROW_ATTR}="${photo.sequence}" ${EXHIBIT_PHOTO_FILE_ATTR}="${escapeHtml(photo.fileName)}" ` +
    `${EXHIBIT_PHOTO_SHA_ATTR}="${photo.sha256}" ${EXHIBIT_PHOTO_SHA512_ATTR}="${photo.sha512}" ${EXHIBIT_PHOTO_TIME_ATTR}="${escapeHtml(photo.capturedAtIso)}" ${EXHIBIT_PHOTO_BYTES_ATTR}="${photo.byteLength}" ${EXHIBIT_PHOTO_FILE_TIME_ATTR}="${escapeHtml(photo.deviceFileTimeIso)}" ${EXHIBIT_PHOTO_METHOD_ATTR}="${photo.captureMethod}"${orientationAttrs(photo.orientation)}>` +
    `<td>${photo.sequence}</td>` +
    `<td>${escapeHtml(photo.fileName)}</td>` +
    `<td>${escapeHtml(describePhotoTime(photo, timeZone))}</td>` +
    `<td class="fn-exhibit-hash">SHA-256: ${photo.sha256}<br>SHA-512: ${photo.sha512}</td>` +
    `</tr>`
  );
}

export function renderExhibitSetHtml(set: ExhibitSet, timeZone: string): string {
  const { reference, subject, location, description } = set.details;
  const count = set.photos.length;
  const metaRows = [
    subject ? metaRow('Photographing', subject) : '',
    location ? metaRow('Location', location) : '',
    metaRow('Photos', `${count}, numbered 1 to ${count} in the order taken`),
    metaRow('Capture', describeCaptureMethods(set)),
    metaRow('Set history', describeHistory(set)),
  ].join('');
  const photoRows = set.photos.map((_, i) => photoRow(set, i, timeZone)).join('');

  return (
    `<div class="${EXHIBIT_SET_ROOT_CLASS}" ${EXHIBIT_SET_MARKER_ATTR}="${EXHIBIT_SET_SCHEMA_VERSION}" ` +
    `${EXHIBIT_REFERENCE_ATTR}="${escapeHtml(reference)}" ${EXHIBIT_SUBJECT_ATTR}="${escapeHtml(subject)}" ` +
    `${EXHIBIT_LOCATION_ATTR}="${escapeHtml(location)}">` +
    `<p class="fn-exhibit-title"><strong>Exhibit Photos: ${escapeHtml(reference)}</strong></p>` +
    (description ? `<p class="fn-exhibit-description">${escapeHtml(description)}</p>` : '') +
    `<table class="fn-exhibit-meta"><tbody>${metaRows}</tbody></table>` +
    `<table class="fn-exhibit-photos"><thead><tr><th>#</th><th>File</th><th>Taken (in-app) or received</th><th>Hashes at capture</th></tr></thead>` +
    `<tbody>${photoRows}</tbody></table>` +
    `<p class="fn-exhibit-lock">Exhibit photo set. Photos are locked in the order taken and cannot be edited or removed individually.</p>` +
    `</div>`
  );
}

function readAttr(tag: string, name: string): string | null {
  const match = new RegExp(`${name}="([^"]*)"`).exec(tag);
  return match ? unescapeHtml(match[1]) : null;
}

export function unescapeHtml(text: string): string {
  return text
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');
}

function isEnumValue<T extends string>(values: Record<string, T>, value: string | null): value is T {
  return value !== null && (Object.values(values) as string[]).includes(value);
}

function readOrientation(tag: string): AppliedOrientation | null {
  const rotation = Number(readAttr(tag, EXHIBIT_PHOTO_ROTATION_ATTR));
  const source = readAttr(tag, EXHIBIT_PHOTO_ROTATION_SOURCE_ATTR);
  const method = readAttr(tag, EXHIBIT_PHOTO_ROTATION_METHOD_ATTR);
  if (readAttr(tag, EXHIBIT_PHOTO_ROTATION_ATTR) === null || !isQuarterTurn(rotation)) {
    return null;
  }
  if (!isEnumValue(RotationSource, source) || !isEnumValue(RotationMethod, method)) {
    return null;
  }
  const device = readAttr(tag, EXHIBIT_PHOTO_DEVICE_ORIENTATION_ATTR);
  const edit = readAttr(tag, EXHIBIT_PHOTO_EXIF_EDIT_ATTR);
  const cameraSha = readAttr(tag, EXHIBIT_PHOTO_CAMERA_SHA_ATTR);
  return {
    captureRotation: rotation,
    rotationSource: source,
    method,
    ...(device === null ? {} : { deviceOrientation: device === NO_DEVICE_ORIENTATION ? NO_DEVICE_ORIENTATION : Number(device) }),
    ...(edit === null ? {} : { exifEdit: edit }),
    ...(cameraSha === null ? {} : { cameraSha256: cameraSha }),
  };
}

function withOrientation(entry: ExhibitPhotoManifestEntry, tag: string): ExhibitPhotoManifestEntry {
  const orientation = readOrientation(tag);
  return orientation ? { ...entry, orientation } : entry;
}

const ROOT_TAG_PATTERN = new RegExp(`<div class="${EXHIBIT_SET_ROOT_CLASS}" ${EXHIBIT_SET_MARKER_ATTR}="\\d+"[^>]*>`);

function findRootTag(noteText: string | null | undefined): string | null {
  const match = typeof noteText === 'string' ? ROOT_TAG_PATTERN.exec(noteText) : null;
  return match ? match[0] : null;
}

export function isExhibitSetHtml(noteText: string | null | undefined): boolean {
  return findRootTag(noteText) !== null;
}

export function parseExhibitSetManifest(noteText: string | null | undefined): ExhibitSetManifest | null {
  const rootTag = findRootTag(noteText);
  if (!rootTag) {
    return null;
  }
  const rowTags = (noteText as string).match(new RegExp(`<tr[^>]*${EXHIBIT_PHOTO_ROW_ATTR}="\\d+"[^>]*>`, 'g')) ?? [];
  const photos: ExhibitPhotoManifestEntry[] = rowTags
    .map((tag) => withOrientation({
      sequence: Number(readAttr(tag, EXHIBIT_PHOTO_ROW_ATTR)),
      fileName: readAttr(tag, EXHIBIT_PHOTO_FILE_ATTR) ?? '',
      sha256: readAttr(tag, EXHIBIT_PHOTO_SHA_ATTR) ?? '',
      sha512: readAttr(tag, EXHIBIT_PHOTO_SHA512_ATTR) ?? '',
      capturedAtIso: readAttr(tag, EXHIBIT_PHOTO_TIME_ATTR) ?? '',
      deviceFileTimeIso: readAttr(tag, EXHIBIT_PHOTO_FILE_TIME_ATTR) ?? '',
      captureMethod: readAttr(tag, EXHIBIT_PHOTO_METHOD_ATTR) ?? '',
      byteLength: Number(readAttr(tag, EXHIBIT_PHOTO_BYTES_ATTR) ?? 0),
    }, tag))
    .sort((a, b) => a.sequence - b.sequence);

  return {
    schemaVersion: Number(readAttr(rootTag, EXHIBIT_SET_MARKER_ATTR)),
    reference: readAttr(rootTag, EXHIBIT_REFERENCE_ATTR) ?? '',
    subject: readAttr(rootTag, EXHIBIT_SUBJECT_ATTR) ?? '',
    location: readAttr(rootTag, EXHIBIT_LOCATION_ATTR) ?? '',
    photos,
  };
}
